import {
    CheckCircleOutlined,
    CloudDownloadOutlined,
    DesktopOutlined,
    ExclamationCircleOutlined,
    SafetyCertificateOutlined,
    WarningOutlined,
} from '@ant-design/icons';
import {
    Alert,
    Button,
    Card,
    Col,
    Collapse,
    Descriptions,
    Empty,
    Row,
    Select,
    Space,
    Spin,
    Table,
    Tag,
    Typography,
    message,
} from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import EidMiddlewareCard from '../components/EidMiddlewareCard';
import { useAuth } from '../contexts/AuthContext';
import { parseContentDispositionFilename } from '../domain/downloads';
import {
    type HardwareServiceRelease,
    type ReleaseArchitecture,
    type ReleasePlatform,
    type ReleaseTarget,
    buildReleaseDownloadPath,
    detectReleaseTarget,
    findReleaseForTarget,
    formatArtifactSize,
    isRestrictedChannel,
    listAvailableTargets,
    sortReleasesForDisplay,
} from '../domain/hardwareServiceReleases';
import { useApiRequest } from '../hooks/useApiRequest';
import useQuery from '../hooks/useQuery';

const { Title, Paragraph, Text } = Typography;

interface ReleaseListResponse {
    success: boolean;
    data: HardwareServiceRelease[];
}

/** Colour per channel. Restricted channels are warning-coloured wherever they appear. */
const CHANNEL_COLOR: Record<string, string> = {
    stable: 'green',
    qa: 'orange',
    internal: 'volcano',
};

/** The instruction sections, in the order an operator meets them. */
// `prerequisites` leads because it is the only section describing something this installer
// cannot do for the operator: the document reader's vendor runtime is supplied externally and is
// deliberately neither bundled nor downloaded. Installation staff who never open it still get a
// working service - printing and eID need nothing extra - which is exactly why the document
// reader's requirement has to be stated somewhere they will look rather than inferred from a
// capability that quietly reports itself unavailable.
const GUIDE_SECTIONS = [
    'prerequisites',
    'install',
    'status',
    'upgrade',
    'repair',
    'uninstall',
] as const;

const HardwareServicePage = () => {
    const { t } = useTranslation();
    const { user } = useAuth();
    const { token, API_BASE_URL } = useApiRequest();

    const isAdmin = user?.role === 'ADMIN';

    const { data, loading, error } = useQuery<ReleaseListResponse>({
        path: '/hardware-service/releases',
    });

    const releases = useMemo(() => sortReleasesForDisplay(data?.data ?? []), [data]);

    // Only non-withdrawn builds are installable. An administrator still sees the withdrawn
    // ones in the table below, because the record of a withdrawal is worth having in front of
    // the person who might repeat it — but nothing offers them for download.
    const installable = useMemo(
        () => releases.filter(release => release.yankedAt === null),
        [releases],
    );

    const availableTargets = useMemo(() => listAvailableTargets(installable), [installable]);

    const [chosenTarget, setChosenTarget] = useState<ReleaseTarget | null>(null);
    const [downloading, setDownloading] = useState(false);

    // The browser's suggestion is a default, not a decision: an operator preparing the machine
    // next to them needs to be able to override it, so the selectors stay editable and this
    // only supplies the initial value.
    const suggestedTarget = useMemo(() => detectReleaseTarget(navigator.userAgent), []);
    const effectiveTarget = chosenTarget ?? suggestedTarget;

    const selectedRelease = useMemo(
        () => findReleaseForTarget(installable, effectiveTarget),
        [installable, effectiveTarget],
    );

    const platformOptions = useMemo(() => {
        const platforms = new Set(availableTargets.map(target => target.platform));

        return [...platforms].map(platform => ({
            value: platform,
            label: t(`hardwareService.platform.${platform}`),
        }));
    }, [availableTargets, t]);

    const architectureOptions = useMemo(() => {
        const architectures = new Set(
            availableTargets
                .filter(target => target.platform === effectiveTarget.platform)
                .map(target => target.architecture),
        );

        return [...architectures].map(architecture => ({
            value: architecture,
            label: t(`hardwareService.architecture.${architecture}`),
        }));
    }, [availableTargets, effectiveTarget.platform, t]);

    /**
     * Download through the authorized route.
     *
     * A direct `fetch` rather than `useRequest`, following the pattern already established by
     * the report exports: the shared hook parses JSON, and this response is an installer. The
     * bearer token still travels, so the backend authorizes and audits exactly as it does for
     * every other call.
     */
    const handleDownload = useCallback(async () => {
        if (!selectedRelease) {
            return;
        }

        setDownloading(true);

        try {
            const response = await fetch(
                `${API_BASE_URL}${buildReleaseDownloadPath(selectedRelease)}`,
                { headers: { Authorization: `Bearer ${token}` } },
            );

            if (!response.ok) {
                message.error(t('hardwareService.downloadFailed'));
                return;
            }

            const blob = await response.blob();
            const filename =
                parseContentDispositionFilename(response.headers.get('Content-Disposition')) ??
                selectedRelease.filename;

            const objectUrl = URL.createObjectURL(blob);
            const anchor = document.createElement('a');
            anchor.href = objectUrl;
            anchor.download = filename;
            document.body.appendChild(anchor);
            anchor.click();
            document.body.removeChild(anchor);
            URL.revokeObjectURL(objectUrl);
        } catch (downloadError) {
            console.error('Hardware service download failed:', downloadError);
            message.error(t('hardwareService.downloadFailed'));
        } finally {
            setDownloading(false);
        }
    }, [API_BASE_URL, selectedRelease, t, token]);

    const columns = useMemo(
        () => [
            {
                title: t('hardwareService.table.version'),
                dataIndex: 'version',
                key: 'version',
            },
            {
                title: t('hardwareService.table.target'),
                key: 'target',
                render: (_: unknown, row: HardwareServiceRelease) => {
                    const platform = t(`hardwareService.platform.${row.platform}`);
                    const architecture = t(`hardwareService.architecture.${row.architecture}`);

                    return `${platform} · ${architecture}`;
                },
            },
            {
                title: t('hardwareService.table.channel'),
                dataIndex: 'channel',
                key: 'channel',
                render: (channel: string) => (
                    <Tag color={CHANNEL_COLOR[channel]}>
                        {t(`hardwareService.channel.${channel}`)}
                    </Tag>
                ),
            },
            {
                title: t('hardwareService.table.size'),
                dataIndex: 'sizeBytes',
                key: 'sizeBytes',
                render: (sizeBytes: number) => formatArtifactSize(sizeBytes),
            },
            {
                title: t('hardwareService.table.signature'),
                dataIndex: 'signed',
                key: 'signed',
                render: (signed: boolean) =>
                    signed ? (
                        <Tag color="green" icon={<SafetyCertificateOutlined />}>
                            {t('hardwareService.signed')}
                        </Tag>
                    ) : (
                        <Tag color="warning" icon={<WarningOutlined />}>
                            {t('hardwareService.unsigned')}
                        </Tag>
                    ),
            },
            {
                title: t('hardwareService.table.status'),
                key: 'status',
                render: (_: unknown, row: HardwareServiceRelease) =>
                    row.yankedAt === null ? (
                        <Tag color="blue">{t('hardwareService.statusPublished')}</Tag>
                    ) : (
                        <Tag color="red">{t('hardwareService.statusWithdrawn')}</Tag>
                    ),
            },
        ],
        [t],
    );

    if (loading) {
        return (
            <div
                style={{ padding: 24, textAlign: 'center' }}
                data-testid="hardware-service-loading"
            >
                <Spin size="large" />
            </div>
        );
    }

    return (
        <div style={{ padding: 24 }} data-testid="hardware-service-page">
            <Title level={2}>
                <DesktopOutlined /> {t('hardwareService.title')}
            </Title>
            <Paragraph type="secondary">{t('hardwareService.subtitle')}</Paragraph>

            {error && (
                <Alert
                    type="error"
                    showIcon
                    message={t('hardwareService.loadError')}
                    style={{ marginBottom: 16 }}
                    data-testid="hardware-service-error"
                />
            )}

            {!error && installable.length === 0 && (
                // Not an error state. Before the first stable publish this is what every
                // ordinary operator sees, and it has to read as "nothing yet" rather than
                // "something is broken".
                <Card style={{ marginBottom: 16 }} data-testid="hardware-service-empty">
                    <Empty
                        image={Empty.PRESENTED_IMAGE_SIMPLE}
                        description={
                            <Space direction="vertical">
                                <Text strong>{t('hardwareService.noReleasesTitle')}</Text>
                                <Text type="secondary">
                                    {isAdmin
                                        ? t('hardwareService.noReleasesAdmin')
                                        : t('hardwareService.noReleasesUser')}
                                </Text>
                            </Space>
                        }
                    />
                </Card>
            )}

            {installable.length > 0 && (
                <Card
                    title={t('hardwareService.chooseTitle')}
                    style={{ marginBottom: 16 }}
                    data-testid="hardware-service-chooser"
                >
                    <Row gutter={[16, 16]}>
                        <Col xs={24} sm={12} md={8}>
                            <Text strong>{t('hardwareService.platformLabel')}</Text>
                            <Select
                                style={{ width: '100%', marginTop: 8 }}
                                value={effectiveTarget.platform}
                                options={platformOptions}
                                data-testid="hardware-service-platform"
                                onChange={(platform: ReleasePlatform) => {
                                    const firstForPlatform = availableTargets.find(
                                        target => target.platform === platform,
                                    );

                                    setChosenTarget({
                                        platform,
                                        architecture:
                                            firstForPlatform?.architecture ??
                                            effectiveTarget.architecture,
                                    });
                                }}
                            />
                        </Col>
                        <Col xs={24} sm={12} md={8}>
                            <Text strong>{t('hardwareService.architectureLabel')}</Text>
                            <Select
                                style={{ width: '100%', marginTop: 8 }}
                                value={effectiveTarget.architecture}
                                options={architectureOptions}
                                data-testid="hardware-service-architecture"
                                onChange={(architecture: ReleaseArchitecture) =>
                                    setChosenTarget({
                                        platform: effectiveTarget.platform,
                                        architecture,
                                    })
                                }
                            />
                        </Col>
                    </Row>

                    {selectedRelease === null ? (
                        <Alert
                            style={{ marginTop: 16 }}
                            type="info"
                            showIcon
                            message={t('hardwareService.noBuildForTarget')}
                            data-testid="hardware-service-no-build"
                        />
                    ) : (
                        <>
                            {isRestrictedChannel(selectedRelease.channel) && (
                                // Labelled unmistakably, per the release architecture: a build
                                // outside the stable channel is never offered without saying so.
                                <Alert
                                    style={{ marginTop: 16 }}
                                    type="warning"
                                    showIcon
                                    icon={<ExclamationCircleOutlined />}
                                    message={t('hardwareService.restrictedChannelTitle', {
                                        channel: t(
                                            `hardwareService.channel.${selectedRelease.channel}`,
                                        ),
                                    })}
                                    description={t('hardwareService.restrictedChannelBody')}
                                    data-testid="hardware-service-restricted"
                                />
                            )}

                            {!selectedRelease.signed && (
                                <Alert
                                    style={{ marginTop: 16 }}
                                    type="warning"
                                    showIcon
                                    message={t('hardwareService.unsignedTitle')}
                                    description={t('hardwareService.unsignedBody')}
                                    data-testid="hardware-service-unsigned"
                                />
                            )}

                            <Descriptions
                                bordered
                                column={1}
                                size="small"
                                style={{ marginTop: 16 }}
                                data-testid="hardware-service-details"
                            >
                                <Descriptions.Item label={t('hardwareService.table.version')}>
                                    {selectedRelease.version}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('hardwareService.table.channel')}>
                                    <Tag color={CHANNEL_COLOR[selectedRelease.channel]}>
                                        {t(`hardwareService.channel.${selectedRelease.channel}`)}
                                    </Tag>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('hardwareService.table.filename')}>
                                    <Text code>{selectedRelease.filename}</Text>
                                </Descriptions.Item>
                                <Descriptions.Item label={t('hardwareService.table.size')}>
                                    {formatArtifactSize(selectedRelease.sizeBytes)}
                                </Descriptions.Item>
                                <Descriptions.Item label={t('hardwareService.table.signature')}>
                                    {selectedRelease.signed ? (
                                        <Tag color="green" icon={<CheckCircleOutlined />}>
                                            {t('hardwareService.signed')}
                                        </Tag>
                                    ) : (
                                        <Tag color="warning" icon={<WarningOutlined />}>
                                            {t('hardwareService.unsigned')}
                                        </Tag>
                                    )}
                                </Descriptions.Item>
                                {/* Shown so an operator can verify what they downloaded against
                                    what the application says it published. */}
                                <Descriptions.Item label={t('hardwareService.table.sha256')}>
                                    <Text copyable code style={{ wordBreak: 'break-all' }}>
                                        {selectedRelease.sha256}
                                    </Text>
                                </Descriptions.Item>
                                {selectedRelease.releaseNotes !== null && (
                                    <Descriptions.Item label={t('hardwareService.table.notes')}>
                                        {selectedRelease.releaseNotes}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>

                            <Button
                                type="primary"
                                icon={<CloudDownloadOutlined />}
                                size="large"
                                loading={downloading}
                                onClick={handleDownload}
                                style={{ marginTop: 16 }}
                                data-testid="hardware-service-download"
                            >
                                {t('hardwareService.download')}
                            </Button>
                        </>
                    )}
                </Card>
            )}

            {/* Beneath the download card, and deliberately its own card: the service above is ours
                to distribute, this one is the ministry's, and an operator must never confuse the
                two when something is missing. */}
            <EidMiddlewareCard />

            <Card
                title={t('hardwareService.guideTitle')}
                style={{ marginBottom: 16 }}
                data-testid="hardware-service-guide"
            >
                <Collapse
                    items={GUIDE_SECTIONS.map(section => ({
                        key: section,
                        label: t(`hardwareService.guide.${section}.title`),
                        children: (
                            <Paragraph style={{ whiteSpace: 'pre-line', marginBottom: 0 }}>
                                {t(`hardwareService.guide.${section}.body`)}
                            </Paragraph>
                        ),
                    }))}
                />
            </Card>

            {releases.length > 0 && (
                <Card title={t('hardwareService.allReleases')} data-testid="hardware-service-table">
                    <Table
                        rowKey={row => `${row.version}-${row.platform}-${row.architecture}`}
                        dataSource={releases}
                        columns={columns}
                        pagination={false}
                        size="small"
                        scroll={{ x: true }}
                    />
                </Card>
            )}
        </div>
    );
};

export default HardwareServicePage;
